import {inject, InjectionToken} from '@angular/core';
import {ApiResponse} from '../model/api-response';
import {catchError, EMPTY, map, Observable, of, switchMap} from 'rxjs';
import {Orgs} from '../model/org.model';
import {HttpClient} from '@angular/common/http';
import {getApiUrl} from '../helpers/env.helper';
import {exponentialBackoffRetry} from '../operators/retry.operator';
import { AuthService } from './auth.service';

interface OrgsResponse {
  success: boolean;
  organizations: Orgs;
}

export interface OrgService {
  getOrgs(): Observable<ApiResponse<Orgs>>
  setActiveOrg(id: string): Observable<unknown>;
  getActiveOrgId(): string | undefined;
}

export const ORG_SERVICE = new InjectionToken<OrgService>('OrgService', {
  providedIn: 'root',
  factory: () => {
    const httpClient = inject(HttpClient);
    const authService = inject(AuthService);
    return new OrgServiceImpl(httpClient, authService);
  },
});

class OrgServiceImpl implements OrgService {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
  ) {}

  getActiveOrgId(): string | undefined {
    const token = this.authService.getTokenAttributes('active_org') as { id: string } | undefined;
    return token?.id;
  }

  getOrgs(): Observable<ApiResponse<Orgs>> {
    return getApiUrl('iam').pipe(
      switchMap(url => this.http.get<OrgsResponse>(`${url}/orgs`).pipe(
        exponentialBackoffRetry(),
        map(res => res.organizations),
        map(orgs => ({
          data: orgs,
        })),
        catchError(err => of({
          error: err,
          data: undefined,
        }))
      ))
    ) ;
  }

  setActiveOrg(id: string) {
    return getApiUrl('iam').pipe(
      switchMap(url => this.http.put(`${url}/user/switch-org`, {orgId: id}).pipe(
        exponentialBackoffRetry(),
        catchError(err => {
          console.error('Error switching org', err);
          return EMPTY;
        })
      ))
    )
  }
}
